import { useMemo } from 'react';
import { useAppSelector } from '@store/hooks';
import {
  selectActiveSafetyRows,
  selectClosedSafetyRows,
  selectEquipmentTypeRows,
  selectManagerListRows,
  selectWorkforceRows,
  type SafetyRow,
} from '@store/selectors/monitor.selectors';
import type { IReplayRail } from '../replay/replayRail';
import { useReplayViewContext } from '../replay/ReplayViewContext';

type IWorkforceSource = Omit<IReplayRail, 'safetyRows'>;

interface ISafetySource {
  active: SafetyRow[];
  closed: SafetyRow[];
  /** Replayed: no row is open now, so the views drop their LIVE section. */
  replaying: boolean;
}

const NO_ROWS: SafetyRow[] = [];

/** The Workforce tab's on-site lists: live, or — while a day is replayed — the frame's at the playhead. */
export function useWorkforceSource(): IWorkforceSource {
  const professionRows = useAppSelector(selectWorkforceRows);
  const managerRows = useAppSelector(selectManagerListRows);
  const equipmentRows = useAppSelector(selectEquipmentTypeRows);
  const live = useMemo(() => ({ professionRows, managerRows, equipmentRows }), [professionRows, managerRows, equipmentRows]);
  return useReplayViewContext()?.rail ?? live;
}

/**
 * The safety lists. Live: open alerts, then closed ones. Replayed: the day's
 * alerts up to the playhead, all in the log — a past alert's close time is not
 * known, so none is shown as open.
 */
export function useSafetySource(): ISafetySource {
  const active = useAppSelector(selectActiveSafetyRows);
  const closed = useAppSelector(selectClosedSafetyRows);
  const replayRows = useReplayViewContext()?.rail.safetyRows;
  return useMemo(
    () =>
      replayRows
        ? { active: NO_ROWS, closed: replayRows, replaying: true }
        : { active, closed, replaying: false },
    [replayRows, active, closed],
  );
}
